import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCreateProject } from '@/hooks/mutations/useCreateProject';
import { isValidGitHubUrl, getProductionUrl } from '@/lib/utils';

const createProjectSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(50, 'Project name must be less than 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Project name can only contain lowercase letters, numbers, and hyphens'),
  github_url: z
    .string()
    .min(1, 'GitHub URL is required')
    .refine((url) => isValidGitHubUrl(url), 'Must be a valid GitHub repository URL'),
  branch: z
    .string()
    .max(100, 'Branch name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9._/-]*$/, 'Invalid branch name')
    .optional()
    .transform((val) => val || 'main'),
});

type CreateProjectForm = z.infer<typeof createProjectSchema>;

export default function CreateProject() {
  const navigate = useNavigate();
  const { mutate: createProject, isPending } = useCreateProject();
  const [previewDomain, setPreviewDomain] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CreateProjectForm>({
    resolver: zodResolver(createProjectSchema),
  });

  const projectName = watch('name');

  // Update preview domain as user types
  useState(() => {
    if (projectName) {
      setPreviewDomain(getProductionUrl(projectName));
    } else {
      setPreviewDomain('');
    }
  });

  const onSubmit = (data: CreateProjectForm) => {
    createProject(data, {
      onSuccess: (project) => {
        navigate(`/projects/${project.id}`);
      },
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create New Project</h1>
        <p className="mt-2 text-gray-600">Deploy your GitHub repository</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
          <CardDescription>
            Enter your project information to get started
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <Input
                id="name"
                placeholder="my-awesome-app"
                {...register('name')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
              {projectName && !errors.name && (
                <p className="mt-1 text-sm text-gray-500">
                  Production URL: <span className="font-medium">{previewDomain}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="github_url" className="block text-sm font-medium text-gray-700 mb-2">
                GitHub Repository *
              </label>
              <Input
                id="github_url"
                placeholder="https://github.com/username/repository"
                {...register('github_url')}
                className={errors.github_url ? 'border-red-500' : ''}
              />
              {errors.github_url && (
                <p className="mt-1 text-sm text-red-600">{errors.github_url.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Must be a public GitHub repository URL
              </p>
            </div>

            <div>
              <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-2">
                Branch
              </label>
              <Input
                id="branch"
                placeholder="main"
                {...register('branch')}
                className={errors.branch ? 'border-red-500' : ''}
              />
              {errors.branch && (
                <p className="mt-1 text-sm text-red-600">{errors.branch.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Default branch to deploy (defaults to main)
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/projects')}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
